import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// One loaded race of the AI Scraper. The store is not persisted, so a refresh clears it: the client's rule is that
// nothing is cached and every Load checks ERA and our DB again.

const messageOf = (error) => {
  const message = error.response && error.response.data && error.response.data.message;
  if (Array.isArray(message)) return message.join(", ");
  return message || error.message || "Something went wrong";
};

export const loadRace = createAsyncThunk("aiScraper/loadRace", async ({ date, race }, { rejectWithValue }) => {
  try {
    const res = await axios.get(`${window.env.API_URL}/aiscraper/race`, { params: { date, race } });
    return res.data;
  } catch (error) {
    if (error.response && error.response.status === 404) return rejectWithValue("ERA has no such race on this date.");
    return rejectWithValue(messageOf(error));
  }
});

// After a save: our DB is read again, ERA stays as loaded.
export const refreshRace = createAsyncThunk("aiScraper/refreshRace", async (_, { getState }) => {
  const { era, picks } = getState().aiScraper;
  const res = await axios.post(`${window.env.API_URL}/aiscraper/compare`, { era, picks });
  return res.data.comparison;
});

// Our race days first, at once; ERA's season list replaces them when it answers.
export const loadCalendar = createAsyncThunk("aiScraper/loadCalendar", async (_, { dispatch }) => {
  const ours = await axios.get(`${window.env.API_URL}/aiscraper/calendar`, { params: { ours: 1 } });
  dispatch(setCalendar(ours.data.days));
  const res = await axios.get(`${window.env.API_URL}/aiscraper/calendar`);
  return res.data.days;
});

// After a save: our race and result counts again, from our DB only, so ERA is not asked twice.
export const refreshCalendarCounts = createAsyncThunk("aiScraper/refreshCalendarCounts", async () => {
  const ours = await axios.get(`${window.env.API_URL}/aiscraper/calendar`, { params: { ours: 1 } });
  return ours.data.days;
});

const aiScraperSlice = createSlice({
  name: "aiScraper",
  initialState: {
    era: null,
    comparison: null,
    loading: false,
    error: null,
    picks: {}, // which record the admin chose when several fit a name
    keptOurs: {}, // differences the admin kept: never saved, they come back on the next Load
    silks: {}, // horse number -> silk the admin chose
    calendar: [], // race days for the date picker
  },
  reducers: {
    pickRecord(state, action) {
      const { key, id, candidate } = action.payload;
      state.picks[key] = id;
      if (state.comparison && Array.isArray(state.comparison.runners)) {
        state.comparison.runners.forEach((r) => {
          ["owner", "trainer", "jockey", "sire", "dam", "damsire"].forEach((role) => {
            const ref = r[role];
            if (ref && ref.era && ref.era.name) {
              const rKey = `${ref.label}:${String(ref.era.name)
                .toUpperCase()
                .replace(/\([A-Z]{2,4}\)/g, " ")
                .replace(/[.,'`’\-()]/g, " ")
                .replace(/\s+/g, " ")
                .trim()}`;
              if (rKey === key) {
                const cand = candidate || (ref.candidates || []).find((c) => c._id === id);
                ref.status = "MATCHED";
                ref.dbId = id;
                ref.dbName = cand ? cand.name : ref.era.name;
                ref.candidates = [];
                ref.picked = true;
              }
            }
          });
        });
      }
    },
    keepOurs(state, action) {
      action.payload.forEach((key) => {
        state.keptOurs[key] = true;
      });
    },
    chooseSilk(state, action) {
      state.silks[action.payload.horseNo] = action.payload.id;
    },
    setLoadError(state, action) {
      state.error = action.payload;
    },
    setCalendar(state, action) {
      state.calendar = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadRace.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.era = null;
        state.comparison = null;
        state.picks = {};
        state.keptOurs = {};
        state.silks = {};
      })
      .addCase(loadRace.fulfilled, (state, action) => {
        state.loading = false;
        state.era = action.payload.era;
        state.comparison = action.payload.comparison;
      })
      .addCase(loadRace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(loadCalendar.fulfilled, (state, action) => {
        state.calendar = action.payload;
      })
      .addCase(refreshCalendarCounts.fulfilled, (state, action) => {
        const counts = new Map(action.payload.map((day) => [day.date, day]));
        state.calendar.forEach((day) => {
          const ours = counts.get(day.date);
          day.races = ours ? ours.races : [];
          day.withHorses = ours ? ours.withHorses || [] : [];
          day.withResult = ours ? ours.withResult : [];
        });
        // A day we just added races to that ERA's list did not have.
        counts.forEach((day, date) => {
          if (!state.calendar.some((d) => d.date === date)) state.calendar.push(day);
        });
      })
      .addCase(refreshRace.fulfilled, (state, action) => {
        state.comparison = action.payload;
      });
  },
});

export const { pickRecord, keepOurs, chooseSilk, setLoadError, setCalendar } = aiScraperSlice.actions;
export default aiScraperSlice.reducer;
